import { slugify } from '@/utils/string';
import clsx from 'clsx';
import { AlertCircle } from 'lucide-react';
import React from 'react';

export interface AppTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  label?: React.ReactNode;
  error?: string;
  hint?: string;
  textareaClassName?: string;
}

const AppTextarea = React.forwardRef<HTMLTextAreaElement, AppTextareaProps>(
  (
    { label, error, hint, textareaClassName = '', id, ...textareaProps },
    ref
  ) => {
    const textareaId =
      id ??
      (typeof label === 'string' ? `textarea-${slugify(label)}` : undefined);
    const isDisabled = textareaProps.disabled;

    const borderClass =
      error && !isDisabled
        ? 'border-danger'
        : !isDisabled
          ? 'border-grey'
          : 'border-grey3';

    const bgClass = isDisabled ? 'bg-grey4' : error ? 'bg-dangerLight' : '';

    return (
      <div>
        {label && (
          <label
            htmlFor={textareaId}
            className="mb-1.5 block text-sm font-medium text-textDark"
          >
            {label}
          </label>
        )}
        <textarea
          ref={ref}
          id={textareaId}
          className={clsx(
            'w-full min-h-[108px] resize-y rounded-[10px] border-[1.5px] bg-lighter px-3.5 py-3 text-[15px] leading-normal text-textDark outline-none transition-colors placeholder:text-grey10 focus:bg-white focus:border-primary',
            borderClass,
            bgClass,
            isDisabled && 'text-grey10 cursor-not-allowed resize-none',
            textareaClassName
          )}
          disabled={isDisabled}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${textareaId}-error` : undefined}
          {...textareaProps}
        />
        {error ? (
          <p
            id={textareaId ? `${textareaId}-error` : undefined}
            className="mt-1 flex items-center gap-1 text-[13px] text-danger"
          >
            <AlertCircle className="size-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : hint ? (
          <p className="mt-1 text-[11.5px] leading-snug text-grey10">{hint}</p>
        ) : null}
      </div>
    );
  }
);

AppTextarea.displayName = 'AppTextarea';

export default AppTextarea;
